import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import LiveRecorder from './liveRecorder.js';
import { useWebRecorder } from './recorders/useWebRecorder.js';
import { reportService } from '../services/index.js';
import { writeToClipboard } from './transcriptUtils.js';
import MicVisualizer from './recorders/MicVisualizer.js';
import { FaSpinner, FaMicrophone, FaPlay, FaPaperPlane, FaTimes, FaCrown } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext.js';
import Card from '../components/Card/Card.js';
import Button from '../components/buttons/Button.js';
import { isElectron } from '../utils/platform.js';
import { useMicrophoneName } from '../hooks/useMicrophoneName.js';

export default function LiveRecorderPage() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const isGoldOrEnterprise = useMemo(() => {
    return Boolean(
      profile?.isGoldOrEnterprise === true ||
      profile?.isgoldorenterprise === true ||
      profile?.subscription?.planType === 'gold' ||
      profile?.subscription?.planType === 'enterprise' ||
      profile?.subscription?.plan === 'gold' ||
      profile?.subscription?.plan === 'enterprise'
    );
  }, [profile]);
  const webRecorder = useWebRecorder(true); // Disable VAD for live recorder
  const { microphoneName, refreshMicrophoneName } = useMicrophoneName();
  const [isSending, setIsSending] = useState(false);
  const [isLiveProcessing, setIsLiveProcessing] = useState(false);
  const isUsageLimitReached = false;
  const [isFakeRecording, setIsFakeRecording] = useState(false);
  const toggleRecordShortcut = '';
  const recordingDurationRef = useRef(0);
  const [isFormattingEnabled, setIsFormattingEnabled] = useState(() => {
    try {
      const v = localStorage.getItem('liveFormatEnabled');
      return v === 'true';
    } catch (_) { return false; }
  });
  const [textareaVisible, setTextareaVisible] = useState(!isElectron); // Always visible on web
  const [textareaValue, setTextareaValue] = useState('');

  // History navigation state
  const [historyEntries, setHistoryEntries] = useState(() => {
    try {
      const stored = localStorage.getItem('liveRecorderTextHistory');
      return stored ? JSON.parse(stored).history : [];
    } catch {
      return [];
    }
  });
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(() => {
    try {
      const stored = localStorage.getItem('liveRecorderTextHistory');
      return stored ? JSON.parse(stored).currentIndex : -1;
    } catch {
      return -1;
    }
  });

  const MAX_HISTORY_ENTRIES = 15;

  const startRecording = webRecorder.startRecording;
  const stopRecording = webRecorder.stopRecording;
  
  // Save history to localStorage
  const saveHistoryToStorage = useCallback((history, index) => {
    try {
      localStorage.setItem('liveRecorderTextHistory', JSON.stringify({
        history,
        currentIndex: index
      }));
    } catch (e) {
      console.error('Failed to save history:', e);
    }
  }, []);
  
  // Save current text to history (only if not empty)
  const saveCurrentToHistory = useCallback(() => {
    const text = (textareaValue || '').trim();
    if (!text) return; // Don't save empty entries
    
    setHistoryEntries(prev => {
      let newHistory = [...prev];
      const now = Date.now();
      
      if (currentHistoryIndex >= 0 && currentHistoryIndex < newHistory.length) {
        // Update existing entry only if it's not empty
        newHistory[currentHistoryIndex] = {
          id: newHistory[currentHistoryIndex].id,
          text,
          timestamp: now
        };
      } else {
        // Add new entry
        const newEntry = { id: now, text, timestamp: now };
        newHistory.push(newEntry);
        // Limit history size
        if (newHistory.length > MAX_HISTORY_ENTRIES) {
          newHistory = newHistory.slice(-MAX_HISTORY_ENTRIES);
        }
        setCurrentHistoryIndex(newHistory.length - 1);
      }
      
      saveHistoryToStorage(newHistory, currentHistoryIndex >= 0 ? currentHistoryIndex : newHistory.length - 1);
      return newHistory;
    });
  }, [textareaValue, currentHistoryIndex, saveHistoryToStorage]);

  const sendStoredAudio = useCallback(async (audioBlob, shouldFormat) => {


    // Safety check: don't send if recording was too short (< 500ms)
    const recordingDuration = recordingDurationRef.current || 0;
    if (recordingDuration < 0.5) {
      
      setIsLiveProcessing(false);
      setIsSending(false);
      setIsFakeRecording(false);
      return;
    }

    try {
      setIsSending(true);
      setIsLiveProcessing(true);
      const fileName = audioBlob.type?.includes('wav') ? 'audio.wav' : 'audio.webm';
      const fileData = new File([audioBlob], fileName, { type: audioBlob.type || 'audio/webm' });
      const result = await reportService.sendLiveVoice(
        fileData,
        null,
        { shouldMettreEnForme: !!shouldFormat, durationSeconds: recordingDuration }
      );
      const text = result?.content || '';
      if (text) {
        // Only append to textarea if it's visible
        if (textareaVisible) {
          // Calculate full text before updating state (for clipboard copy and history)
          const fullText = textareaValue ? (textareaValue + ' ' + text) : text;

          setTextareaValue(fullText);

          // Auto-save to history (outside of setTextareaValue to avoid StrictMode double-call)
          if (fullText.trim()) {
            const entry = { id: Date.now(), text: fullText, timestamp: Date.now() };
            setHistoryEntries(prevHistory => {
              let newHistory = [...prevHistory];
              if (currentHistoryIndex >= 0 && currentHistoryIndex < newHistory.length) {
                newHistory[currentHistoryIndex] = entry;
              } else {
                newHistory.push(entry);
                if (newHistory.length > MAX_HISTORY_ENTRIES) {
                  newHistory = newHistory.slice(-MAX_HISTORY_ENTRIES);
                }
              }
              const newIndex = currentHistoryIndex >= 0 ? currentHistoryIndex : newHistory.length - 1;
              setCurrentHistoryIndex(newIndex);
              saveHistoryToStorage(newHistory, newIndex);
              return newHistory;
            });
          }

        }
        if (window?.electronAPI?.invoke) {
          // If textarea not visible, copy and attempt direct insertion
          if (!textareaVisible) {
            try { if ((text || '').trim()) { await writeToClipboard(text); } } catch (_) {}
            try { await window.electronAPI.invoke('recorder:insert-text', text); } catch (_) {}
          }
        }
      }
    } catch (_) {
    } finally {
      setIsLiveProcessing(false);
      setIsSending(false);
      setIsFakeRecording(false);
    }
  }, [textareaVisible, textareaValue]);

  const triggerRecord = useCallback(async () => {
    if (!webRecorder.isRecording) {
      setIsFakeRecording(true);
      await startRecording();
    } else {
      setIsFakeRecording(true);
      const finalAudio = await stopRecording();
      try {
        setIsLiveProcessing(true);
        const fileName = finalAudio.type?.includes('wav') ? 'audio.wav' : 'audio.webm';
        const fileData = new File([finalAudio], fileName, { type: finalAudio.type || 'audio/webm' });
        const result = await reportService.sendLiveVoice(
          fileData,
          null,
          { shouldMettreEnForme: !!isFormattingEnabled, durationSeconds: (webRecorder.recordingDuration || 0) / 1000 }
        );
        const text = result?.content || '';
        if (text) {
          // Only append to textarea if it's visible
          if (textareaVisible) {
            // Calculate full text before updating state (for clipboard copy and history)
            const fullText = textareaValue ? (textareaValue + ' ' + text) : text;

            setTextareaValue(fullText);

            // Auto-save to history (outside of setTextareaValue to avoid StrictMode double-call)
            if (fullText.trim()) {
              const entry = { id: Date.now(), text: fullText, timestamp: Date.now() };
              setHistoryEntries(prevHistory => {
                let newHistory = [...prevHistory];
                if (currentHistoryIndex >= 0 && currentHistoryIndex < newHistory.length) {
                  newHistory[currentHistoryIndex] = entry;
                } else {
                  newHistory.push(entry);
                  if (newHistory.length > MAX_HISTORY_ENTRIES) {
                    newHistory = newHistory.slice(-MAX_HISTORY_ENTRIES);
                  }
                }
                const newIndex = currentHistoryIndex >= 0 ? currentHistoryIndex : newHistory.length - 1;
                setCurrentHistoryIndex(newIndex);
                saveHistoryToStorage(newHistory, newIndex);
                return newHistory;
              });
            }

          }
          if (window?.electronAPI?.invoke) {
            // If textarea not visible, copy and attempt direct insertion
            if (!textareaVisible) {
              try { if ((text || '').trim()) { await writeToClipboard(text); } } catch (_) {}
              try { await window.electronAPI.invoke('recorder:insert-text', text); } catch (_) {}
            }
          }
        }
      } catch (_) {
      } finally {
        setIsLiveProcessing(false);
        setIsFakeRecording(false);
      }
    }
  }, [webRecorder, startRecording, stopRecording, isFormattingEnabled, textareaVisible, textareaValue]);

  // Persist formatting mode
  useEffect(() => {
    try { localStorage.setItem('liveFormatEnabled', String(!!isFormattingEnabled)); } catch (_) {}
  }, [isFormattingEnabled]);

  // Web-only keyboard shortcuts: Ctrl+C to copy, Ctrl+X to cut (only if textarea has > 5 chars)
  useEffect(() => {
    if (isElectron) return; // Only on web

    const handleKeyDown = async (e) => {
      // Only trigger if textarea has more than 5 characters
      if (!textareaValue || textareaValue.length <= 5) return;

      // Ctrl+C or Cmd+C to copy
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        e.preventDefault();
        try { await writeToClipboard(textareaValue); } catch (_) {}
      }
      // Ctrl+X or Cmd+X to cut
      if ((e.ctrlKey || e.metaKey) && e.key === 'x') {
        e.preventDefault();
        try { await writeToClipboard(textareaValue); } catch (_) {}
        setTextareaValue('');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [textareaValue]);

  // IPC: toggle formatting mode
  useEffect(() => {
    if (!window?.electronAPI?.on) return;
    const remove = window.electronAPI.on('toggle-live-format', () => {
      setIsFormattingEnabled((prev) => !prev);
    });
    return () => { if (remove) try { remove(); } catch (_) {} };
  }, []);

  // IPC: show textarea (e.g., when Electron window focused during insert)
  useEffect(() => {
    if (!window?.electronAPI?.on) return;
    const rem = window.electronAPI.on('liveRecorder:textarea:show', (_event, text) => {
      setTextareaVisible(true);
      // Add the text to textarea if provided
      if (text && typeof text === 'string' && text.trim()) {
        setTextareaValue(prev => {
          const newText = prev ? (prev + ' ' + text) : text;
          // Auto-save to history
          if (newText.trim()) {
            const entry = { id: Date.now(), text: newText, timestamp: Date.now() };
            setHistoryEntries(prevHistory => {
              let newHistory = [...prevHistory];
              if (currentHistoryIndex >= 0 && currentHistoryIndex < newHistory.length) {
                newHistory[currentHistoryIndex] = entry;
              } else {
                newHistory.push(entry);
                if (newHistory.length > MAX_HISTORY_ENTRIES) {
                  newHistory = newHistory.slice(-MAX_HISTORY_ENTRIES);
                }
              }
              const newIndex = currentHistoryIndex >= 0 ? currentHistoryIndex : newHistory.length - 1;
              setCurrentHistoryIndex(newIndex);
              saveHistoryToStorage(newHistory, newIndex);
              return newHistory;
            });
          }
          return newText;
        });
      }
    });
    return () => { if (rem) try { rem(); } catch (_) {} };
  }, [currentHistoryIndex, saveHistoryToStorage]);

  // IPC: perform insert-buffer (paste, then clear+close)
  useEffect(() => {
    if (!window?.electronAPI?.on) return;
    const rem = window.electronAPI.on('liveRecorder:insert-buffer', async () => {
      const text = (textareaValue || '').trim();
      if (!text) return;
      try { await window.electronAPI.invoke('insert-text-direct', text); } catch (_) {}
      setTextareaValue('');
      setTextareaVisible(false);
    });
    return () => { if (rem) try { rem(); } catch (_) {} };
  }, [textareaValue]);

  // Resize live recorder window when text area visibility changes
  useEffect(() => {
    try { window.electronAPI?.invoke?.('liveRecorder:setExpanded', !!textareaVisible); } catch (_) {}
  }, [textareaVisible]);

  // Event handler registration - runs only on mount
  useEffect(() => {
    if (!window?.electronAPI?.on) return;

    const handler = async () => {
      
      if (webRecorder.isRecording || isFakeRecording) {
        try {
          // Convert from milliseconds to seconds
          recordingDurationRef.current = (webRecorder.recordingDuration || 0) / 1000;
          const finalAudio = await stopRecording();
          await sendStoredAudio(finalAudio, isFormattingEnabled);
        } catch (e) {
          console.error('[HANDLER] Error stopping:', e);
        }
      } else {
        await triggerRecord();
      }
    };

    const remove = window.electronAPI.on('toggle-live-record', handler);
    return () => {
      
      if (remove) try { remove(); } catch (_) {}
    };
  }, [webRecorder.isRecording, isFakeRecording, triggerRecord, stopRecording, sendStoredAudio, isFormattingEnabled]);

  const handleClearTextarea = useCallback(() => {
    // Clear text and remove from history if it exists
    setTextareaValue('');
    if (currentHistoryIndex >= 0 && currentHistoryIndex < historyEntries.length) {
      setHistoryEntries(prev => {
        const newHistory = prev.filter((_, index) => index !== currentHistoryIndex);
        // Adjust index after removal
        const newIndex = Math.min(currentHistoryIndex, newHistory.length - 1);
        setCurrentHistoryIndex(newHistory.length > 0 ? newIndex : -1);
        // Load the new current entry if it exists
        if (newHistory.length > 0 && newIndex >= 0) {
          setTextareaValue(newHistory[newIndex].text);
        }
        saveHistoryToStorage(newHistory, newHistory.length > 0 ? newIndex : -1);
        return newHistory;
      });
    }
  }, [currentHistoryIndex, historyEntries, saveHistoryToStorage]);

  const handleCutTextarea = useCallback(async () => {
    // Copy to clipboard
    try {
      if (textareaValue) {
        await writeToClipboard(textareaValue);
      }
    } catch (_) {}

    // Clear and update history (same logic as handleClearTextarea)
    setTextareaValue('');
    if (currentHistoryIndex >= 0 && currentHistoryIndex < historyEntries.length) {
      setHistoryEntries(prev => {
        const newHistory = prev.filter((_, index) => index !== currentHistoryIndex);
        const newIndex = Math.min(currentHistoryIndex, newHistory.length - 1);
        setCurrentHistoryIndex(newHistory.length > 0 ? newIndex : -1);
        if (newHistory.length > 0 && newIndex >= 0) {
          setTextareaValue(newHistory[newIndex].text);
        }
        saveHistoryToStorage(newHistory, newHistory.length > 0 ? newIndex : -1);
        return newHistory;
      });
    }

    // Collapse on Electron
    if (isElectron) {
      setTextareaVisible(false);
    }
  }, [textareaValue, currentHistoryIndex, historyEntries, saveHistoryToStorage]);

  const handleCopyTextarea = useCallback(async () => {
    try { await writeToClipboard(textareaValue || ''); } catch (_) {}
  }, [textareaValue]);

  const handleToggleTextarea = useCallback(() => {
    setTextareaVisible(prev => !prev);
  }, []);

  // Navigate backward in history
  const handleNavigateBack = useCallback(() => {
    if (currentHistoryIndex <= 0) {
      // Try to get last inserted text from Electron
      (async () => {
        try {
          const lastText = await window.electronAPI?.invoke?.('liveRecorder:get-last-inserted');
          if (lastText && lastText.trim()) {
            // Save current if not empty
            if (textareaValue.trim()) {
              saveCurrentToHistory();
            }
            // Add as new entry
            const newEntry = { id: Date.now(), text: lastText, timestamp: Date.now() };
            setHistoryEntries(prev => {
              const newHistory = [...prev, newEntry];
              const newIndex = newHistory.length - 1;
              saveHistoryToStorage(newHistory, newIndex);
              setCurrentHistoryIndex(newIndex);
              setTextareaValue(lastText);
              return newHistory;
            });
          }
        } catch (error) {
          console.error('Failed to recover last text:', error);
        }
      })();
      return;
    }
    
    // Save current text to current position
    if (textareaValue.trim() && currentHistoryIndex >= 0) {
      setHistoryEntries(prev => {
        const newHistory = [...prev];
        newHistory[currentHistoryIndex] = {
          ...newHistory[currentHistoryIndex],
          text: textareaValue.trim(),
          timestamp: Date.now()
        };
        saveHistoryToStorage(newHistory, currentHistoryIndex - 1);
        return newHistory;
      });
    }
    
    // Navigate backward
    const newIndex = currentHistoryIndex - 1;
    setCurrentHistoryIndex(newIndex);
    setTextareaValue(historyEntries[newIndex].text);
  }, [currentHistoryIndex, historyEntries, textareaValue, saveCurrentToHistory, saveHistoryToStorage]);

  // Navigate forward in history or create new entry (only if current is not empty)
  const handleNavigateForward = useCallback(() => {
    const currentText = textareaValue.trim();
    
    // Don't allow forward if current entry is empty
    if (!currentText) {
      return;
    }
    
    // Save current text to current position
    if (currentHistoryIndex >= 0 && currentHistoryIndex < historyEntries.length) {
      setHistoryEntries(prev => {
        const newHistory = [...prev];
        newHistory[currentHistoryIndex] = {
          ...newHistory[currentHistoryIndex],
          text: currentText,
          timestamp: Date.now()
        };
        saveHistoryToStorage(newHistory, currentHistoryIndex);
        return newHistory;
      });
    }
    
    // If at end of history, create new empty entry
    if (currentHistoryIndex >= historyEntries.length - 1) {
      const newEntry = { id: Date.now(), text: '', timestamp: Date.now() };
      setHistoryEntries(prev => {
        let newHistory = [...prev];
        // Save current entry if not in history yet
        if (currentHistoryIndex < 0 || currentHistoryIndex >= prev.length) {
          newHistory.push({ id: Date.now() - 1, text: currentText, timestamp: Date.now() });
        }
        // Add new empty entry
        newHistory.push(newEntry);
        if (newHistory.length > MAX_HISTORY_ENTRIES) {
          newHistory = newHistory.slice(-MAX_HISTORY_ENTRIES);
        }
        const newIndex = newHistory.length - 1;
        saveHistoryToStorage(newHistory, newIndex);
        setCurrentHistoryIndex(newIndex);
        setTextareaValue('');
        return newHistory;
      });
    } else {
      // Navigate to existing next entry
      const newIndex = currentHistoryIndex + 1;
      setCurrentHistoryIndex(newIndex);
      setTextareaValue(historyEntries[newIndex].text);
    }
  }, [currentHistoryIndex, historyEntries, textareaValue, saveHistoryToStorage, MAX_HISTORY_ENTRIES]);

  const handleReset = useCallback(async () => {
    try {
      // Stop recording if active
      if (webRecorder.isRecording) {
        try {
          await stopRecording();
        } catch (error) {
          console.error('Error stopping recording:', error);
        }
      }
      
      // Reset all states
      setIsSending(false);
      setIsLiveProcessing(false);
      setIsFakeRecording(false);
      setTextareaValue('');
      setTextareaVisible(false);
      recordingDurationRef.current = 0;
      
      // Clear all history entries
      setHistoryEntries([]);
      setCurrentHistoryIndex(-1);
      
      // Clear localStorage
      try {
        localStorage.removeItem('liveRecorderTextHistory');
      } catch (e) {
        console.error('Failed to clear history from storage:', e);
      }
      
    } catch (error) {
      console.error('Error during reset:', error);
    }
  }, [webRecorder.isRecording, stopRecording]);

  if (!isGoldOrEnterprise) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        padding: 6,
        background: '#f5f5f5'
      }}>
        <Card style={{ maxWidth: 180, textAlign: 'center', padding: '12px' }}>
          <div style={{ marginBottom: 8 }}>
            <FaCrown size={32} style={{ color: '#FFD700' }} />
          </div>
          <p style={{ margin: 0, color: '#666', lineHeight: 1.3, fontSize: 11 }}>
            Réservé aux abonnements Gold et Entreprise
          </p>
        </Card>
      </div>
    );
  }

  return (
    <LiveRecorder
      triggerRecord={triggerRecord}
      microphoneName={microphoneName}
      renderRecordBtnContent={() => {
        const isSmall = true; // live window uses small layout
        const iconSize = 20;
        // Loading spinner while sending/processing
        if (isSending || isLiveProcessing) {
          return <FaSpinner className="fa-spin" size={iconSize} />;
        }
        // MICROPHONE: only if nothing else is happening
        if (!isFakeRecording) {
          return (
            <>
              <FaMicrophone size={iconSize} />
              {isFormattingEnabled ? <FaPaperPlane size={iconSize} style={{ marginLeft: 6 }} /> : null}
            </>
          );
        }
        // WAITING STATE: Ready for next recording - show play icon
        if (isFakeRecording && !webRecorder.isRecording) {
          return <FaPlay size={iconSize} />;
        }
        // RECORDING - Visualizer with voice activity detection
        return (
          <div className="record-inner">
            <MicVisualizer
              active
              voiceActivity={webRecorder.voiceActivity}
              isSilent={webRecorder.isSilent}
              width={45}
              height={32}
              color="#ffffff"
              barWidth={2}
              numBars={10}
              mirror={true}
              className="mic-canvas"
            />
          </div>
        );
      }}
      isUsageLimitReached={isUsageLimitReached}
      toggleRecordShortcut={toggleRecordShortcut}
      isSending={isSending}
      isRecording={webRecorder.isRecording}
      isFakeRecording={isFakeRecording}
      isLiveProcessing={isLiveProcessing}
      isFormattingEnabled={isFormattingEnabled}
      onToggleFormatting={() => setIsFormattingEnabled(prev => !prev)}
      textareaVisible={textareaVisible}
      textareaValue={textareaValue}
      onChangeTextarea={setTextareaValue}
      onClearTextarea={handleClearTextarea}
      onCopyTextarea={handleCopyTextarea}
      onCutTextarea={handleCutTextarea}
      onToggleTextarea={handleToggleTextarea}
      onNavigateBack={handleNavigateBack}
      onNavigateForward={handleNavigateForward}
      onReset={handleReset}
      canNavigateBack={currentHistoryIndex > 0}
      canNavigateForward={textareaValue.trim().length > 0}
      historyPosition={historyEntries.length > 0 ? `${currentHistoryIndex + 1}/${historyEntries.length}` : ''}
      onClose={() => navigate(-1)}
    />
  );
}


